// Lib
import React, { ReactElement } from 'react';

// Utils
import platformSingleton from '../../../platform/platformSingleton';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';
import doesEditorJsonHaveText from '../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';

// Hooks
import { useLimitedElementTiptapContent } from '../../../../common/tiptap/conversion/elementConversion/useLimitedElementTiptapContent';

// Components
import Icon from '../../../components/icons/Icon';
import DocumentDetailViewTiptapEditor from './DocumentDetailViewTiptapEditor';
import { GroupTranslatorProvider, useGroupTranslator } from '../../../components/i18n/GroupTranslationContext';

// Types
import { ImEditorContent, ImMNElement } from '../../../../common/elements/elementModelTypes';
import { DocumentType } from '../../../../common/tiptap/conversion/elementConversion/elementConversion';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';

const EmptyDocumentPreview = () => {
    const groupTranslate = useGroupTranslator<'elements', 'elementTypes.document'>();

    return (
        <div className="EmptyDocumentPreview">
            <Icon name="content-empty" />
            <span className="preview-text">
                {platformSingleton.features.supportsMouse
                    ? groupTranslate('empty.mouse', 'Double click to start typing')
                    : groupTranslate('empty.touch', 'Double tap to start typing')}
            </span>
        </div>
    );
};

interface DocumentDetailViewEditorProps {
    element: ImMNElement;
    textContent: ImEditorContent;
    isFocusedForegroundElement: boolean;
}

const MAX_DETAIL_VIEW_PARAGRAPHS = 20;
const useDocumentTiptapContent = useLimitedElementTiptapContent(MAX_DETAIL_VIEW_PARAGRAPHS);

const DocumentDetailViewEditorContent = (props: DocumentDetailViewEditorProps): ReactElement => {
    const { textContent, element } = props;

    const hasContent = doesEditorJsonHaveText(textContent);

    const elementId = getElementId(element);
    const tiptapContent = useDocumentTiptapContent(textContent, elementId, DocumentType.textContent);

    if (!hasContent) return <EmptyDocumentPreview />;

    const editorId = getMainEditorId(props);
    const editorKey = getMainEditorKey(props);

    return <DocumentDetailViewTiptapEditor textContent={tiptapContent} editorId={editorId} editorKey={editorKey} />;
};

const DocumentDetailViewEditor = (props: DocumentDetailViewEditorProps): ReactElement => (
    <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.document">
        <DocumentDetailViewEditorContent {...props} />
    </GroupTranslatorProvider>
);

export default DocumentDetailViewEditor;
